import { Box, Button, ButtonGroup, Dialog, Divider, IconButton } from "@mui/material";
import { pxToRem } from "../../utils/fontSizeUtility";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/sharp-regular-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import { PlatformDialogTitle } from "./internals/components/PlatformDialogTitle";
import { PlatformDialogActions } from "./internals/components/PlatformDialogActions";
import { PlatformDialogContent } from "./internals/components/PlatformDialogContent";
import { RelativeBox } from "./internals/components/RelativeBox";
import { CenteredCircularProgress } from "./internals/components/CenteredCircularProgress";
import React from "react";
import { NavigateBefore, NavigateNext } from "@mui/icons-material";
import { breakpointValues, ChatBlockTimestampPaper, customBreakpoints } from "../../platform-style";

library.add(faXmark);

interface PlatformDialogProps {
  open: boolean;
  title: string;
  onClose?: () => void;
  closeLabel?: string;
  onSubmit?: () => void;
  submitLabel?: string;
  isSubmitting?: boolean;
  isSubmitDisabled?: boolean;
  children?: React.JSX.Element;
  maxHeight?: number;
  maxWidth?: number;
  destructive?: boolean;
  confirm?: boolean;
  confirmLabel?: string;
  previousButton?: () => void;
  nextButton?: () => void;
  isPreviousButtonDisabled?: boolean;
  isNextButtonDisabled?: boolean;
  submitOnAltEnter?: boolean;
  countOfRecords?: string;
  disableEscapeKeyDown?: boolean;
  optionalControlSlot?: React.JSX.Element;
}

export function PlatformDialog({
  open,
  title,
  onClose,
  closeLabel,
  onSubmit,
  submitLabel = "submit",
  isSubmitting = false,
  isSubmitDisabled = false,
  children,
  maxHeight,
  maxWidth,
  destructive = false,
  confirm = false,
  confirmLabel = "",
  previousButton,
  nextButton,
  isPreviousButtonDisabled = false,
  isNextButtonDisabled = false,
  submitOnAltEnter = false,
  countOfRecords = "",
  disableEscapeKeyDown = false,
  optionalControlSlot,
}: Readonly<PlatformDialogProps>) {
  // TODO: I am a one off that cannot use the core global
  const LAYOUT_TOGGLE_STORAGE_KEY = "ENABLE_ALTERNATE_LAYOUT";
  const useAlternateLayout = localStorage.getItem(LAYOUT_TOGGLE_STORAGE_KEY) === "true";

  const DIALOG_PADDING_TOP = 8;
  const defaultCloseLabel = onSubmit ? "cancel" : "close";
  const closeButtonVariant = onSubmit ? "outlined" : "contained";

  const [isConfirming, setIsConfirming] = React.useState<boolean>(false);
  function onConfirm() {
    setIsConfirming(true);
  }

  function onConfirmBack() {
    setIsConfirming(false);
  }

  return (
    <Dialog
      container={() => document.getElementById("single-spa-main-content") || document.body}
      onClose={onClose}
      disableEscapeKeyDown={disableEscapeKeyDown}
      onKeyDown={(e) => {
        if (submitOnAltEnter && !isSubmitDisabled) {
          if (e.key === "Enter" && e.altKey) {
            if (onSubmit) {
              onSubmit();
            }
          }
        }
      }}
      open={open}
      sx={{
        position: "absolute",
      }}
      slotProps={{
        paper: {
          style: {
            maxHeight: maxHeight ? pxToRem(maxHeight + DIALOG_PADDING_TOP) : "100%",
            marginTop: 0,
            marginBottom: 0,
            maxWidth: maxWidth ? pxToRem(maxWidth) : undefined,
            borderRadius: useAlternateLayout ? pxToRem(28) : pxToRem(4),
          },
        },
      }}
    >
      <PlatformDialogTitle destructive={destructive}>
        {title}
        <IconButton
          sx={{
            color: "inherit",
            marginRight: pxToRem(-13),
          }}
          size={"small"}
          aria-label="close icon"
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
      </PlatformDialogTitle>
      <PlatformDialogContent>{children}</PlatformDialogContent>
      <Divider />
      <PlatformDialogActions
        sx={{
          justifyContent:
            (previousButton && nextButton) || optionalControlSlot ? "space-between" : "flex-end",
          [customBreakpoints.down(breakpointValues.sm)]: {
            display: "inline-list-item",
            overflow: "scroll",
            overflowY: "scroll",
          },
        }}
      >
        {/* showing the confirmation after submit if the "confirm" property is set to true  */}
        {isConfirming ? (
          <>
            <Box sx={{ marginRight: pxToRem(16) }}>{isConfirming ? confirmLabel : ""}</Box>
            <Button
              variant={closeButtonVariant}
              onClick={onConfirmBack}
              color={destructive ? "error" : "primary"}
            >
              {"back"}
            </Button>
            {onSubmit ? (
              <RelativeBox>
                <Button
                  variant="contained"
                  color={destructive ? "error" : isConfirming ? "success" : "primary"}
                  disabled={isSubmitDisabled || isSubmitting}
                  onClick={onSubmit}
                  sx={{ marginLeft: pxToRem(10) }}
                >
                  {"confirm"}
                </Button>
                {isSubmitting && <CenteredCircularProgress size={24} color={"primary"} />}
              </RelativeBox>
            ) : null}
          </>
        ) : (
          <>
            {/* showing the previous and next buttons if properties exist. */}
            {previousButton && nextButton ? (
              <>
                <ButtonGroup variant="outlined" aria-label="previous next button group">
                  <Button
                    onClick={previousButton}
                    aria-label="previous button"
                    disabled={isPreviousButtonDisabled}
                  >
                    <NavigateBefore sx={{ fontSize: "large" }} />
                  </Button>
                  <Button
                    onClick={nextButton}
                    aria-label="next button"
                    disabled={isNextButtonDisabled}
                  >
                    <NavigateNext sx={{ fontSize: "large" }} />
                  </Button>
                </ButtonGroup>
                <ChatBlockTimestampPaper
                  isUser={false}
                  sx={{ background: "none", marginTop: "auto", flexWrap: "nowrap" }}
                >
                  {countOfRecords}
                </ChatBlockTimestampPaper>
              </>
            ) : (
              optionalControlSlot
            )}
            <RelativeBox>
              <Button
                variant={closeButtonVariant}
                onClick={onClose}
                color={destructive ? "primary" : "inherit"}
              >
                {closeLabel ?? defaultCloseLabel}
              </Button>

              {/* showing the submit button */}
              {onSubmit ? (
                <>
                  {" "}
                  <Button
                    variant="contained"
                    color={destructive ? "error" : isConfirming ? "success" : "primary"}
                    disabled={isSubmitDisabled || isSubmitting}
                    onClick={confirm ? onConfirm : onSubmit}
                    sx={{
                      marginLeft: pxToRem(10),
                    }}
                  >
                    {submitLabel}
                  </Button>
                  {isSubmitting && <CenteredCircularProgress size={24} color={"primary"} />}
                </>
              ) : null}
            </RelativeBox>
          </>
        )}
      </PlatformDialogActions>
    </Dialog>
  );
}
